import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ConfirmModal from "../../../../../components/ConfirmModal/ConfirmModal";
import { triggerLogout } from "../../../../../auth/logout";
import { storyAssets } from "../../constants/assets";
import { TrackingEvents } from "../../../../Utilities/analytics";

import {
  conversationBufferDuration,
  convertStringToID,
  getRelatedBlockIDs,
} from "../../utils/storyHelpers";
import { getBgImageForBlock } from "../../utils/storyHelpers";
import WebLottiePlayer from "../WebLottiePlayer";
import WebSnowfall from "../WebSnowfall";
import WebShadowButton from "../WebShadowButton";
import ChatBubble from "../../../../components/ChatBubble/ChatBubble";
import WebAudioPlayer from "../WebAudioPlayer";
import { WebLoader } from "../../../../components/Loader";
import "./WebConversationBlock.css";
import { useNavigate } from "react-router-dom";
import { BackgroundMusicButton } from "../../../../components/BackgroundMusic";
import { clearStoryData } from "../../../../store/storySlice";
import { useAppDispatch } from "../../../../store/hooks";
import predQstnIcon from "../../../../appAssets/predQstnIcon.png";
import LottieWebPlayer from "../../../AppJourneyPage/LottieWebPlayer";

interface WebConversationBlockProps {
  conversationValue: Record<string, unknown>;
  updateApiCall: (_linkId?: string | string[], _updateParent?: string) => void;
  allBlocksData: Record<string, unknown>;
  isLoading: boolean;
  isMilestoneActivated: boolean;
  hideProgressBar: (_value: boolean) => void;
  stopAudio?: boolean;
  // navigation: WebStoryNavigation;
  isFocused?: boolean;
  contentBaseUrl?: string;
  onTrackEvent?: (event: string, props?: Record<string, unknown>) => void;
  onShowJourneySplash?: (onComplete: () => void) => void;
  isOnboardingCompleted: boolean;
  showPredictionBtn?: boolean;
  onPredictionBtnPress?: () => void;
}

const WebConversationBlock: React.FC<WebConversationBlockProps> = ({
  conversationValue,
  updateApiCall,
  allBlocksData,
  isLoading,
  isMilestoneActivated,
  hideProgressBar,
  stopAudio = false,
  // navigation,
  isFocused = true,
  contentBaseUrl,
  onTrackEvent,
  onShowJourneySplash,
  isOnboardingCompleted,
  showPredictionBtn = false,
  onPredictionBtnPress,
}) => {
  const playIconJson = require("../../../../appAssets/jsons/IconPlay.json");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [conversationIndex, setConversationIndex] = useState(0);
  const [objectIndex, setObjectIndex] = useState(0);
  const [showYeti, setShowYeti] = useState(true);
  const [showButton, setShowButton] = useState(false);
  const [enabledMusic, setEnabledMusic] = useState(true);
  const [playbackRate, setPlaybackRate] = useState<1 | 1.25 | 1.5 | 2>(() => {
    try {
      const stored = sessionStorage.getItem("storyPlaybackRate");
      const parsed = stored ? parseFloat(stored) : 1;
      return ([1, 1.25, 1.5, 2].includes(parsed) ? parsed : 1) as
        | 1
        | 1.25
        | 1.5
        | 2;
    } catch {
      return 1;
    }
  });
  const [showPlaybackModal, setShowPlaybackModal] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [resumeKey, setResumeKey] = useState(0);
  const playbackRateRef = useRef<number>(1);
  playbackRateRef.current = playbackRate;
  const isPausedRef = useRef(false);
  isPausedRef.current = isPaused;
  const prevStopAudioRef = useRef(stopAudio);
  const [showTilesArr, setShowTilesArr] = useState<
    Array<{
      linkTo?: string;
      duration?: string;
      audio?: string;
      icon?: string;
      title?: string;
    }>
  >([]);
  const [renderAudioUrl, setRenderAudioUrl] = useState("");
  const conversations = useMemo(
    () =>
      (conversationValue?.conversations as Array<Record<string, unknown>>) ??
      [],
    [conversationValue?.conversations],
  );
  const [lastBubbleData, setLastBubbleData] = useState<{
    content: { content?: string; textColor?: string; audio?: string } | null;
    isOwnMessage: boolean;
    textcolor: string;
    audio: string;
  }>({ content: null, isOwnMessage: false, textcolor: "", audio: "" });
  // Hamburger dropdown state
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Prevents double-advancement when both onAudioEnd and the fallback timer fire
  const advancedRef = useRef(false);
  // Tracks the pending audio-end delay so it can be cancelled by effect cleanup
  const audioEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Stable ref so the advancement effect doesn't re-run on every parent re-render
  const conversationValueRef = useRef(conversationValue);
  conversationValueRef.current = conversationValue;

  const handleStageClick = useCallback(
    (e: React.MouseEvent) => {
      if (showTilesArr.length > 0 || showButton) return;
      const target = e.target as HTMLElement;
      if (target.closest("button") || target.closest("a")) return;
      if (showPlaybackModal) {
        setShowPlaybackModal(false);
        setIsPaused(false);
        setResumeKey((k) => k + 1);
      } else {
        setIsPaused(true);
        setShowPlaybackModal(true);
      }
    },
    [showPlaybackModal, showTilesArr.length, showButton],
  );

  const advanceObject = useCallback(() => {
    if (advancedRef.current || isPausedRef.current) return;
    advancedRef.current = true;
    setShowYeti((prev) => !prev);
    setObjectIndex((prev) => prev + 1);
  }, []);

  // Schedules advancement 500ms after audio ends; timer is tracked so cleanup can cancel it
  const handleAudioEnd = useCallback(() => {
    if (audioEndTimerRef.current !== null)
      clearTimeout(audioEndTimerRef.current);
    audioEndTimerRef.current = setTimeout(() => {
      audioEndTimerRef.current = null;
      advanceObject();
    }, 500);
  }, [advanceObject]);

  useEffect(() => {
    setShowYeti(true);
    setObjectIndex(0);
    setShowButton(false);
    setShowTilesArr([]);
    setConversationIndex(0);
    hideProgressBar(false);
  }, [conversationValue?.conversations, hideProgressBar]);

  useEffect(() => {
    if (!isFocused) {
      setEnabledMusic(true);
    }
  }, [isFocused]);

  // Detect stopAudio going from true → false (lesson plan dismissed) and restart audio
  useEffect(() => {
    if (prevStopAudioRef.current && !stopAudio) {
      setResumeKey((k) => k + 1);
    }
    prevStopAudioRef.current = stopAudio;
  }, [stopAudio]);

  useEffect(() => {
    // Cancel any stale audio-end delay from the previous step before resetting the guard.
    // Without this, an untracked setTimeout from handleAudioEnd could fire after the guard
    // is reset, causing a spurious extra advance that skips the next character's turn.
    if (audioEndTimerRef.current !== null) {
      clearTimeout(audioEndTimerRef.current);
      audioEndTimerRef.current = null;
    }
    // Reset guard for every new object/conversation
    advancedRef.current = false;

    // While paused or externally stopped (e.g. lesson plan modal), cancel timers
    if (isPaused || stopAudio) return;

    if (conversationIndex <= conversations.length - 1) {
      const currentConversation =
        (conversations[conversationIndex]?.object as Array<
          Record<string, unknown>
        >) || [];
      const lastConversation =
        (conversations[conversations.length - 1]?.object as Array<
          Record<string, unknown>
        >) || [];

      if (objectIndex < currentConversation.length) {
        const audioUrl = String(currentConversation[objectIndex]?.audio ?? "");

        if (audioUrl) {
          // Primary trigger: onAudioEnd on ChatBubble fires advanceObject.
          // Fallback timer fires if audio fails to load/play (network issues).
          const rawDuration = parseInt(
            String(currentConversation[objectIndex]?.duration ?? "8000"),
            10,
          );
          // Guard against NaN (empty/missing duration) — NaN makes setTimeout fire at 0ms
          const safeDuration = isNaN(rawDuration) ? 8000 : rawDuration;
          const fallbackMs =
            (safeDuration + conversationBufferDuration) /
            playbackRateRef.current;
          const timer = setTimeout(advanceObject, fallbackMs);
          return () => {
            clearTimeout(timer);
            if (audioEndTimerRef.current !== null) {
              clearTimeout(audioEndTimerRef.current);
              audioEndTimerRef.current = null;
            }
          };
        }

        // No audio — advance after a brief pause so content is visible
        const timer = setTimeout(advanceObject, 800 / playbackRateRef.current);
        return () => {
          clearTimeout(timer);
          if (audioEndTimerRef.current !== null) {
            clearTimeout(audioEndTimerRef.current);
            audioEndTimerRef.current = null;
          }
        };
      }

      const lastObject = currentConversation[currentConversation.length - 1]
        ?.content
        ? currentConversation[currentConversation.length - 1]
        : currentConversation[currentConversation.length - 2];

      setLastBubbleData({
        content: lastObject as {
          content?: string;
          textColor?: string;
          audio?: string;
        },
        isOwnMessage:
          lastConversation.length === 1
            ? false
            : lastConversation.length === 2 &&
              Boolean(
                (
                  currentConversation[currentConversation.length - 1] as {
                    content?: string;
                  }
                )?.content?.length,
              ),
        textcolor: String(
          (lastObject as { textColor?: string })?.textColor ?? "",
        ),
        audio: String((lastObject as { audio?: string })?.audio ?? ""),
      });
      setObjectIndex(0);
      setConversationIndex((prev) => prev + 1);
    }

    if (conversationIndex === conversations.length - 1) {
      const userActions = conversationValueRef.current?.userActions as Array<{
        component?: string;
        tiles?: unknown[];
      }>;
      const action = userActions?.find(
        (act) => act?.component === "block.button-input",
      );
      const tileAction = userActions?.find(
        (act) => act?.component === "block.tiles-input",
      );
      if (action) {
        setShowButton(true);
      }
      if (tileAction?.tiles) {
        setShowTilesArr(tileAction.tiles as typeof showTilesArr);
        hideProgressBar(true);
      }
    }
  }, [
    conversationIndex,
    objectIndex,
    conversations,
    hideProgressBar,
    advanceObject,
    isPaused,
    stopAudio,
  ]);

  const renderConversation = () => {
    const currentConversation =
      (conversations[conversationIndex]?.object as Array<
        Record<string, unknown>
      >) || [];
    const isSingleObject = currentConversation.length === 1;

    // Still within this conversation step — check if current object has content
    if (objectIndex < currentConversation.length) {
      const currentObject = currentConversation[objectIndex];
      if (
        !currentObject?.content ||
        typeof currentObject.content !== "string"
      ) {
        // This character has no content for this turn — show nothing (don't bleed previous conversation)
        return null;
      }
      return (
        <ChatBubble
          key={`${conversationIndex}-${objectIndex}-${resumeKey}`}
          tailSide={isSingleObject || showYeti ? "left" : "right"}
          content={String(currentObject.content)}
          textColor={String(currentObject.textColor ?? "") || undefined}
          audioUri={String(currentObject.audio ?? "") || undefined}
          stopAudio={isPaused || stopAudio}
          onAudioEnd={handleAudioEnd}
          playbackRate={playbackRate}
        />
      );
    }

    // Conversation exhausted — keep the last bubble visible while transitioning (no audio replay)
    const lastText = lastBubbleData?.content?.content;
    if (!lastText?.trim()) return null;
    return (
      <ChatBubble
        tailSide={lastBubbleData.isOwnMessage ? "right" : "left"}
        content={lastText}
        textColor={lastBubbleData.textcolor || undefined}
        audioUri={undefined}
        stopAudio={true}
      />
    );
  };

  // const renderLastBubble = () => {
  //   const text = lastBubbleData?.content?.content;
  //   if (!text?.trim()) {
  //     return null;
  //   }
  //   return (
  //     <ChatBubble
  //       tailSide={lastBubbleData.isOwnMessage ? "right" : "left"}
  //       content={text}
  //       textColor={lastBubbleData.textcolor || undefined}
  //       audioUri={lastBubbleData.audio || undefined}
  //       stopAudio={stopAudio}
  //     />
  //   );
  // };

  const conversationIndexVal =
    conversationIndex > conversations.length - 1
      ? conversationIndex - 1
      : conversationIndex;

  const currentConvObj =
    (conversations[conversationIndexVal]?.object as Array<
      Record<string, unknown>
    >) || [];
  const isSingleConv = currentConvObj.length === 1;
  const activeTailSide: "left" | "right" = showButton
    ? lastBubbleData.isOwnMessage
      ? "right"
      : "left"
    : isSingleConv || showYeti
      ? "left"
      : "right";

  const renderCharacterObject = (
    containerClass: string,
    marginLeft?: string,
  ) => {
    const yetiBlock = String(
      (
        conversations[conversations.length - 1]?.object as Array<
          Record<string, unknown>
        >
      )?.[0]?.characterImage ?? "",
    );
    const charUri =
      String(
        (
          conversations[conversationIndexVal]?.object as Array<
            Record<string, unknown>
          >
        )?.[0]?.characterImage ?? "",
      ) || yetiBlock;
    const secondChar = String(
      (
        conversations[conversationIndexVal]?.object as Array<
          Record<string, unknown>
        >
      )?.[1]?.characterImage ?? "",
    );

    return (
      <div
        className={containerClass}
        style={marginLeft ? { marginLeft } : undefined}
      >
        <WebLottiePlayer
          source={charUri}
          className="web-convo-yeti-left"
          autoPlay
          loop
        />
        {secondChar && (
          <WebLottiePlayer
            source={secondChar}
            className="web-convo-yeti-right"
            autoPlay
            loop
          />
        )}
      </div>
    );
  };

  if (isLoading) {
    return <WebLoader />;
  }

  const bgImage = getBgImageForBlock(
    conversationValue as { bgImage?: string; bgImageTab?: string },
  );

  const doLogout = () => {
    dispatch(clearStoryData());
    // setMenuOpen(false);
    audioRef.current?.pause();
    triggerLogout("manual");
  };

  const handleLogout = () => setShowLogoutConfirm(true);

  return (
    <div className="web-convo-root">
      <WebSnowfall maxSize={2} />
      <div
        className="web-convo-bg"
        style={{ backgroundImage: bgImage ? `url(${bgImage})` : undefined }}
      />

      <header className="web-convo-header">
        <button
          type="button"
          className="web-convo-header-btn"
          onClick={() => {
            onTrackEvent?.(TrackingEvents.navigate_to_journey_back);
            // navigate(WEB_SCREENS.ApplicationNavigatorStack, {
            //   screen: WEB_SCREENS.MyJourneyScreen,
            // });
            navigate("/web/appJourneyPage");
          }}
        >
          <WebLottiePlayer
            source={storyAssets.lottie.journeyIcon}
            className="web-convo-header-icon"
            autoPlay={false}
            loop={false}
          />
        </button>

        {showPredictionBtn && (
          <button
            type="button"
            className="web-convo-header-btn"
            onClick={onPredictionBtnPress}
            style={{
              border: "1px solid #9747ff",
              boxShadow: "4px 4px 0 #9747ff",
              borderRadius: 12,
              marginLeft: "10%",
            }}
          >
            <img
              src={predQstnIcon}
              alt="prediction question"
              className="web-convo-header-icon"
              style={{ objectFit: "contain" }}
            />
          </button>
        )}

        <div className="web-convo-header-right">
          <>
            {conversationIndex <=
            conversations.length -
              1 /* Conversation in progress → no replay */ ? null : (
              // <button
              //   type="button"
              //   className="web-convo-header-btn"
              //   onClick={() => {
              //     if (isPaused) setResumeKey((k) => k + 1);
              //     setIsPaused((p) => !p);
              //   }}
              //   aria-label={isPaused ? "Resume" : "Pause"}
              // >
              //   <WebLottiePlayer
              //     source={
              //       isPaused
              //         ? storyAssets.lottie.playerPlayBtn
              //         : storyAssets.lottie.playerPauseBtn
              //     }
              //     className="web-convo-header-icon"
              //     autoPlay
              //     loop={false}
              //   />
              // </button>
              /* Conversation finished → replay */
              <button
                type="button"
                className="web-convo-header-btn"
                onClick={() => {
                  setConversationIndex(0);
                  setObjectIndex(0);
                  setShowButton(false);
                  setIsPaused(false);
                }}
                aria-label="Replay"
              >
                <WebLottiePlayer
                  source={storyAssets.lottie.replayIcon}
                  className="web-convo-header-icon"
                  autoPlay={false}
                  loop={false}
                />
              </button>
            )}
            {allBlocksData?.bgMusic &&
              typeof allBlocksData.bgMusic === "string" && (
                <div className="web-convo-header-btn web-story-music-btn">
                  <BackgroundMusicButton
                    audioUrl={allBlocksData.bgMusic as string}
                    defaultPlaying={!enabledMusic}
                    glowIcon={isOnboardingCompleted === false}
                    isOnBoardingDone={isOnboardingCompleted === true}
                    onPlayStateChange={(playing) => setEnabledMusic(!playing)}
                  />
                </div>
              )}
            <button
              type="button"
              className="web-convo-header-btn"
              // onClick={() => setMenuOpen((o) => !o)}
              onClick={() => handleLogout()}
            >
              <WebLottiePlayer
                source={storyAssets.lottie.logoutIcon}
                className="web-convo-menu-icon"
                autoPlay={false}
                loop={false}
              />
            </button>
            {menuOpen && (
              <div className="rooms__dropdown" ref={menuRef}>
                <button className="rooms__dropdown-item" onClick={handleLogout}>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </>
        </div>
      </header>

      <div className="web-convo-stage" onClick={handleStageClick}>
        {showTilesArr.length < 1 &&
          renderCharacterObject("web-convo-yeti-container")}

        {showTilesArr.length < 1 && !isMilestoneActivated && (
          <div
            className={`web-convo-bubble-area web-convo-bubble-area--${activeTailSide}`}
          >
            {renderConversation()}
          </div>
        )}

        <div className="web-convo-actions">
          {showButton &&
            (
              conversationValue?.userActions as Array<{
                id?: string;
                component?: string;
                buttonText?: string;
              }>
            )?.map(
              (action) =>
                action.component === "block.button-input" && (
                  <div key={action.id} className="web-convo-explore-btn">
                    <WebShadowButton
                      title={action.buttonText ?? "Continue"}
                      onPress={() => {
                        const text = action.buttonText?.toLowerCase() ?? "";
                        if (text.includes("yes i do")) {
                          const parentId = (
                            conversationValue?.parentBlock as { id?: string }
                          )?.id;

                          console.log(parentId, "parentIdparentId");
                          parentId
                            ? updateApiCall([parentId], "")
                            : updateApiCall();
                        } else if (text.includes("no questions")) {
                          const related = getRelatedBlockIDs(
                            (allBlocksData?.blocks as never[]) ?? [],
                            Number(conversationValue?.id),
                          );
                          const relatedIds = related
                            .map((item) => String(item?.id ?? ""))
                            .filter(Boolean);
                          console.log(related, "related");

                          updateApiCall(relatedIds, "updateParent");
                        } else {
                          updateApiCall();
                        }
                      }}
                    />
                  </div>
                ),
            )}
        </div>

        {showTilesArr.length > 0 && (
          <>
            <div className="web-convo-tiles-panel">
              {renderCharacterObject("web-convo-tiles-yeti")}
              <h2 className="web-convo-tiles-title">
                Do you have a question, my friend?
              </h2>
              <div className="web-convo-tiles-scroll">
                {showTilesArr.map((item, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className="web-convo-tile"
                    onClick={() => {
                      const linkId = convertStringToID(item?.linkTo ?? "");
                      if (item?.duration && item?.audio) {
                        setRenderAudioUrl(item.audio);
                        setTimeout(
                          () => {
                            updateApiCall([linkId], "");
                            setConversationIndex(0);
                            hideProgressBar(false);
                            setRenderAudioUrl("");
                          },
                          parseInt(item.duration, 10) + 2000,
                        );
                      } else {
                        updateApiCall([linkId], "");
                        setConversationIndex(0);
                      }
                    }}
                  >
                    {item?.icon && (
                      <WebLottiePlayer
                        source={item.icon}
                        className="web-convo-tile-icon"
                        autoPlay={false}
                        loop={false}
                      />
                    )}
                    <span className="web-convo-tile-text">{item.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {renderAudioUrl && (
        <WebAudioPlayer uri={renderAudioUrl} contentBaseUrl={contentBaseUrl} />
      )}

      {/* ── Full-screen playback overlay (covers header + stage) ── */}
      {showPlaybackModal && (
        <div
          className="web-convo-playback-overlay"
          onClick={(e) => {
            if (
              (e.target as HTMLElement).classList.contains(
                "web-convo-playback-overlay",
              )
            ) {
              setShowPlaybackModal(false);
              setIsPaused(false);
              setResumeKey((k) => k + 1);
            }
          }}
        >
          <div className="web-convo-playback-modal">
            <button
              type="button"
              className="web-convo-playback-restart"
              aria-label="Restart"
              onClick={() => {
                setConversationIndex(0);
                setObjectIndex(0);
                setShowButton(false);
                setIsPaused(false);
                setResumeKey((k) => k + 1);
                setShowPlaybackModal(false);
              }}
            >
              <WebLottiePlayer
                source={storyAssets.lottie.replayIcon}
                className="web-convo-playback-restart-icon"
                autoPlay={false}
                loop={false}
              />
            </button>

            <button
              type="button"
              className="web-convo-playback-play"
              aria-label="Play"
              onClick={() => {
                setIsPaused(false);
                setResumeKey((k) => k + 1);
                setShowPlaybackModal(false);
              }}
            >
              {/* <span className="web-convo-playback-play-icon" /> */}
              <LottieWebPlayer
                source={playIconJson}
                style={{ width: "80%", height: "80%" }}
                autoPlay
                loop
              />
            </button>

            <div className="web-convo-playback-speed-pill">
              {([1, 1.25, 1.5] as const).map((rate) => (
                <button
                  key={rate}
                  type="button"
                  className={`web-convo-playback-speed-opt${playbackRate === rate ? " web-convo-playback-speed-opt--active" : ""}`}
                  onClick={() => {
                    try {
                      sessionStorage.setItem("storyPlaybackRate", String(rate));
                    } catch {}
                    setPlaybackRate(rate);
                  }}
                >
                  {rate}x
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showLogoutConfirm}
        title="Confirm Logout"
        message="Are you sure you want to log out?"
        confirmText="Log out"
        cancelText="Cancel"
        onConfirm={() => {
          setShowLogoutConfirm(false);
          doLogout();
        }}
        onCancel={() => setShowLogoutConfirm(false)}
      />
    </div>
  );
};

export default WebConversationBlock;
